import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'

export function getStatusBadgeColor(status: string): {
  bg: string
  color: string
} {
  switch (status) {
    case 'draft':
    case 'pending':
    case 'cancelled':
      return { bg: colors.NEUTRAL_100, color: colors.NEUTRAL_0 }
    case 'processing':
      return { bg: `${colors.PRIMARY}33`, color: colors.PRIMARY }
    case 'applied':
      return { bg: `${colors.SUCCESS_300}33`, color: colors.SUCCESS_300 }
    case 'partially_drafted':
      return { bg: `${colors.WARNING_300}33`, color: colors.WARNING_300 }
    case 'failed':
      return { bg: `${colors.ERROR_200}33`, color: colors.ERROR_200 }
    default:
      return { bg: colors.NEUTRAL_100, color: colors.NEUTRAL_0 }
  }
}

export function getStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'pending':
      return 'Configuring'
    case 'processing':
      return 'Generating'
    case 'partially_drafted':
      return 'Partial'
    case 'failed':
      return 'Failed'
    case 'applied':
      return 'Published'
    case 'cancelled':
      return 'Cancelled'
    default:
      return status
  }
}

/**
 * Derive the effective display status considering published divisions.
 * When the DB status is 'draft' or 'partially_drafted' but at least one
 * division has been published, treat it as 'applied' for display purposes.
 */
export function getEffectiveStatus(
  status: string,
  approvedDivisionCount: number
): string {
  if (
    (status === 'draft' || status === 'partially_drafted') &&
    approvedDivisionCount > 0
  ) {
    return 'applied'
  }
  return status
}

/**
 * Get status label for a flight (simplified: Draft / Published only).
 */
export function getFlightStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'published':
      return 'Published'
    default:
      return status
  }
}

/**
 * Get badge color for a flight status (two schemes only).
 */
export function getFlightStatusBadgeColor(status: string): {
  bg: string
  color: string
} {
  switch (status) {
    case 'published':
      return { bg: `${colors.SUCCESS_300}33`, color: colors.SUCCESS_300 }
    case 'draft':
    default:
      return { bg: colors.NEUTRAL_100, color: colors.NEUTRAL_0 }
  }
}

/**
 * Format a date range for display. Omits year from start if same year as end.
 */
export function formatFlightDateRange(
  startDate: string | null,
  endDate: string | null
): string | null {
  if (!startDate || !endDate) {
    return null
  }
  const start = dayjs(startDate)
  const end = dayjs(endDate)
  if (start.year() === end.year()) {
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
  }
  return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`
}
